import Pusher from 'pusher-js'
import type { Options } from 'pusher-js'

import {
  PusherEventBus,
  ChannelVisibility,
  type ChannelVisibilityEnum,
} from '@webapp/core/libs/PusherEventBus'
import {
  PUSHER_APP_KEY,
  PUSHER_CLUSTER,
} from '@webapp/infrastructure/envs/envs'
import type {
  EventBusDomainEventMap,
  IEventBus,
} from '@webapp/infrastructure/interfaces/services/IEventBus'

type EventBusEndpointConfig = {
  endpoint: string
  headers?: Record<string, string>
  params?: Record<string, string>
}

/**
 * Authentication configuration for EventBus.
 */
type EventBusAuthConfig = {
  /** Authorization configuration for private, private encrypted, or presence channels */
  channelAuthorization?: EventBusEndpointConfig
  /** Authentication configuration for user-based features */
  userAuthentication?: EventBusEndpointConfig
}

/**
 * Configuration options for creating an EventBus instance.
 */
export interface EventBusConfig {
  /** The name of the Pusher channel to subscribe to */
  channelName: string
  /** The visibility of the Pusher channel */
  visibility?: ChannelVisibilityEnum
  /** Authentication configuration for private or presence channels */
  auth?: EventBusAuthConfig
}

/**
 * Factory function to create an EventBus instance using Pusher as the underlying
 * provider.
 *
 * Currently assumes one bus instance per channel. If multiple concurrent channels are
 * needed later, consider introducing a shared Pusher client instance to avoid
 * multiple socket connections.
 *
 * Authorization is required for private, private encrypted, and presence channels,
 * while user authentication is only needed for user-based features available in
 * presence channels.
 *
 * @template T - The domain event map type defining event names and their data types.
 *               Transport events are automatically included and do not need to be specified.
 *               Defaults to `EventBusDomainEventMap` if not specified.
 * @param config - Configuration for the EventBus instance.
 * @returns An instance of `IEventBus`.
 *
 * @example
 * ```typescript
 * // Public channel (no authentication required)
 * const publicEventBus = createEventBus({
 *   channelName: 'notifications',
 * })
 *
 * // Private channel (requires channel authorization)
 * const privateEventBus = createEventBus({
 *   channelName: 'user-updates',
 *   visibility: ChannelVisibility.PRIVATE,
 *   auth: {
 *     channelAuthorization: {
 *       endpoint: '/pusher/auth',
 *       headers: {
 *         'X-CSRFToken': 'your-csrf-token',
 *       },
 *     },
 *   },
 * })
 * ```
 */
export function createEventBus<
  T extends EventBusDomainEventMap = EventBusDomainEventMap,
>(config: EventBusConfig): IEventBus<T> {
  const { channelName, visibility = ChannelVisibility.PUBLIC, auth } = config

  if (!PUSHER_APP_KEY) {
    throw new Error(
      'Pusher app key is not defined. Please set the PUSHER_APP_KEY environment variable.'
    )
  }
  if (!PUSHER_CLUSTER) {
    throw new Error(
      'Pusher cluster is not defined. Please set the PUSHER_CLUSTER environment variable.'
    )
  }

  const needsChannelAuthorization =
    visibility === ChannelVisibility.PRIVATE ||
    visibility === ChannelVisibility.PRIVATE_ENCRYPTED ||
    visibility === ChannelVisibility.PRESENCE

  if (needsChannelAuthorization && !auth?.channelAuthorization?.endpoint) {
    throw new Error(
      `Channel authorization endpoint is required for ${visibility} channels`
    )
  }

  const pusherOptions: Options = {
    cluster: PUSHER_CLUSTER,
    forceTLS: true,
  }

  if (needsChannelAuthorization && auth?.channelAuthorization) {
    pusherOptions.channelAuthorization = {
      ...auth.channelAuthorization,
      transport: 'ajax',
    }
  }

  if (auth?.userAuthentication) {
    pusherOptions.userAuthentication = {
      ...auth.userAuthentication,
      transport: 'ajax',
    }
  }

  const client = new Pusher(PUSHER_APP_KEY, pusherOptions)

  return new PusherEventBus<T>(client, {
    channelName,
    visibility,
  })
}
